/**
 * VendorImportDialog
 * Supports CSV and Excel (.xlsx/.xls) import.
 * Parses files entirely in the browser → maps columns → previews → POSTs
 * each vendor row to POST /vendor via ApiCall.
 *
 * Expected CSV columns (order-independent, fuzzy-matched):
 *   vendor_name / name / company
 *   contact_name / contact / person
 *   email / e-mail / mail
 *   phone / mobile / tel
 *   address / addr / location
 *   qualification_notes / notes / qualifications
 */
import React, { useState, useRef, useCallback } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Stack, Alert,
  LinearProgress, Stepper, Step, StepLabel, Select, MenuItem,
  FormControl, InputLabel, IconButton, Tooltip, Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { CloudUploadOutlined, CloseRounded, CheckCircle, ErrorOutline } from "@mui/icons-material";
import Iconify from "../iconify";
import ApiCall from "../../utils/apicall";

// ─── helpers ────────────────────────────────────────────────────────────────

const VENDOR_FIELDS = [
  { key: "vendor_name",          label: "Vendor Name *" },
  { key: "contact_name",         label: "Contact Name" },
  { key: "email",                label: "Email" },
  { key: "phone",                label: "Phone" },
  { key: "address",              label: "Address" },
  { key: "qualification_notes",  label: "Qualification Notes" },
  { key: "ignore",               label: "— Ignore this column —" },
];

function guessField(header) {
  const h = (header || "").toLowerCase().replace(/[\s_.,-]/g, "");
  if (/^(vendorname|name|company|vendor)/.test(h)) return "vendor_name";
  if (/^(contactname|contact|person|rep)/.test(h))  return "contact_name";
  if (/^(email|mail|e-?mail)/.test(h))              return "email";
  if (/^(phone|mobile|cell|tel)/.test(h))           return "phone";
  if (/^(address|addr|location|city)/.test(h))      return "address";
  if (/^(qual|notes?|qualification)/.test(h))       return "qualification_notes";
  return "ignore";
}

const DropZone = styled(Box)(({ theme, isDragging }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(5),
  textAlign: "center",
  cursor: "pointer",
  background: isDragging ? theme.palette.action.hover : "transparent",
  transition: "all .2s",
  "&:hover": { borderColor: theme.palette.primary.light, background: theme.palette.action.hover },
}));

const STEPS = ["Upload File", "Map Columns", "Preview & Import"];

// ─── component ───────────────────────────────────────────────────────────────

export default function VendorImportDialog({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [rawHeaders, setRawHeaders] = useState([]);   // original column names
  const [rawRows, setRawRows] = useState([]);          // [{col: val, ...}]
  const [mapping, setMapping] = useState({});          // { colName: fieldKey }
  const [preview, setPreview] = useState([]);          // [{vendor_name, ...}]
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);        // { ok: [], errors: [] }

  // ─── file parsing ──────────────────────────────────────────────────────────

  const parseFile = useCallback((file) => {
    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data, meta }) => {
          setRawHeaders(meta.fields || []);
          setRawRows(data);
          const m = {};
          (meta.fields || []).forEach((h) => { m[h] = guessField(h); });
          setMapping(m);
          setStep(1);
        },
      });
    } else if (["xlsx", "xls"].includes(ext)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!data.length) return;
        const headers = Object.keys(data[0]);
        setRawHeaders(headers);
        setRawRows(data);
        const m = {};
        headers.forEach((h) => { m[h] = guessField(h); });
        setMapping(m);
        setStep(1);
      };
      reader.readAsArrayBuffer(file);
    } else {
      alert("Only .csv, .xlsx, or .xls files are supported");
    }
  }, []);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) parseFile(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  };

  // ─── mapping → preview ────────────────────────────────────────────────────

  const buildPreview = () => {
    const rows = rawRows.map((raw) => {
      const vendor = {};
      Object.entries(mapping).forEach(([col, field]) => {
        if (field !== "ignore" && raw[col] !== undefined) {
          vendor[field] = (vendor[field] || "") + (vendor[field] ? " " : "") + String(raw[col]).trim();
        }
      });
      return vendor;
    }).filter((v) => v.vendor_name && v.vendor_name.trim());
    setPreview(rows);
    setStep(2);
  };

  // ─── import ───────────────────────────────────────────────────────────────

  const runImport = async () => {
    setImporting(true);
    const ok = [];
    const errors = [];

    for (const row of preview) {
      try {
        await ApiCall.post("/vendor", row);
        ok.push(row.vendor_name);
      } catch (err) {
        errors.push({ name: row.vendor_name, msg: err.response?.data?.message || "Failed" });
      }
    }

    setResults({ ok, errors });
    setImporting(false);
    if (ok.length > 0 && onImported) onImported();
  };

  // ─── reset ────────────────────────────────────────────────────────────────

  const handleClose = () => {
    setStep(0);
    setRawHeaders([]);
    setRawRows([]);
    setMapping({});
    setPreview([]);
    setImporting(false);
    setResults(null);
    onClose();
  };

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>Import Vendor List</span>
          <IconButton size="small" onClick={handleClose}><CloseRounded /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>

        {/* ── STEP 0: Upload ── */}
        {step === 0 && (
          <Stack spacing={2}>
            <Alert severity="info" icon={false}>
              Accepted formats: <strong>CSV</strong>, <strong>Excel (.xlsx / .xls)</strong>.
              Required column: <strong>vendor_name</strong> (or "name", "company").
              Optional: contact_name, email, phone, address, qualification_notes.
            </Alert>

            <DropZone
              isDragging={isDragging}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <CloudUploadOutlined sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography variant="h6">Drop your file here</Typography>
              <Typography variant="body2" color="text.secondary">or click to browse</Typography>
              <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                .csv · .xlsx · .xls
              </Typography>
            </DropZone>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileInput}
            />

            <Alert severity="success" icon={false} sx={{ fontSize: 12 }}>
              <strong>CSV template columns:</strong>{" "}
              vendor_name, contact_name, email, phone, address, qualification_notes
            </Alert>
          </Stack>
        )}

        {/* ── STEP 1: Map Columns ── */}
        {step === 1 && (
          <Stack spacing={2}>
            <Alert severity="info">
              {rawHeaders.length} columns detected. Map each column to a vendor field.
              Rows without a <strong>vendor_name</strong> will be skipped.
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Your Column</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sample Value</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Map To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rawHeaders.map((col) => (
                    <TableRow key={col}>
                      <TableCell>{col}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {String(rawRows[0]?.[col] || "").substring(0, 40) || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            value={mapping[col] || "ignore"}
                            onChange={(e) => setMapping({ ...mapping, [col]: e.target.value })}
                          >
                            {VENDOR_FIELDS.map((f) => (
                              <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {/* ── STEP 2: Preview + Results ── */}
        {step === 2 && !results && (
          <Stack spacing={2}>
            {importing && <LinearProgress />}
            <Alert severity="success" icon={false}>
              <strong>{preview.length} valid vendor{preview.length !== 1 ? "s" : ""}</strong> ready to import.
              All will be added with <em>Pending</em> status — approve each one on the Vendors page.
            </Alert>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Vendor Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((v, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell><strong>{v.vendor_name}</strong></TableCell>
                      <TableCell>{v.contact_name || "—"}</TableCell>
                      <TableCell>{v.email || "—"}</TableCell>
                      <TableCell>{v.phone || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {/* ── RESULTS ── */}
        {results && (
          <Stack spacing={2}>
            <Alert severity={results.errors.length === 0 ? "success" : "warning"} icon={false}>
              <strong>{results.ok.length} imported</strong>
              {results.errors.length > 0 && `, ${results.errors.length} failed`}
            </Alert>

            {results.ok.length > 0 && (
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  <CheckCircle sx={{ fontSize: 16, color: "success.main", mr: 0.5, verticalAlign: "middle" }} />
                  Imported successfully:
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {results.ok.map((n) => <Chip key={n} label={n} size="small" color="success" variant="outlined" />)}
                </Stack>
              </Box>
            )}

            {results.errors.length > 0 && (
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom color="error">
                  <ErrorOutline sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                  Failed:
                </Typography>
                {results.errors.map((e, i) => (
                  <Typography key={i} variant="caption" display="block" color="error">
                    {e.name}: {e.msg}
                  </Typography>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {step === 0 && <Button onClick={handleClose}>Cancel</Button>}

        {step === 1 && (
          <>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button
              onClick={buildPreview}
              variant="contained"
              disabled={!Object.values(mapping).includes("vendor_name")}
            >
              Preview ({rawRows.length} rows)
            </Button>
          </>
        )}

        {step === 2 && !results && (
          <>
            <Button onClick={() => setStep(1)} disabled={importing}>Back</Button>
            <Button
              onClick={runImport}
              variant="contained"
              disabled={importing || preview.length === 0}
            >
              {importing ? `Importing…` : `Import ${preview.length} Vendor${preview.length !== 1 ? "s" : ""}`}
            </Button>
          </>
        )}

        {results && (
          <Button onClick={handleClose} variant="contained">Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
